import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Package, LogOut, Copy, Check, MapPin, FileText, User, Pencil, Eye, X, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatInvoiceDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const CustomerDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [userProfile, setUserProfile] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [packagesRes, invoicesRes, profileRes] = await Promise.all([
        axios.get(`${API}/customer/packages`, { headers }),
        axios.get(`${API}/customer/invoices`, { headers }),
        axios.get(`${API}/auth/me`, { headers })
      ]);

      setPackages(packagesRes.data);
      setInvoices(invoicesRes.data);
      setUserProfile(profileRes.data);
      setProfileForm({
        name: profileRes.data.name || '',
        phone: profileRes.data.phone || '',
        address: profileRes.data.address || ''
      });
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/customer/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile(response.data);
      // Update local storage user
      const updatedUser = { ...user, name: response.data.name, us_address: response.data.us_address };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success('Profile updated successfully!');
      setShowEditProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const copyAddress = () => {
    if (user?.us_address) {
      const addressText = `${user.us_address.customer_name}\n${user.us_address.address_line1}\n${user.us_address.address_line2}\n${user.us_address.city}, ${user.us_address.state} ${user.us_address.zip}\n${user.us_address.country || 'USA'}\nPhone: ${user.us_address.contact || '+1 592 635 0592'}`;
      navigator.clipboard.writeText(addressText);
      setCopied(true);
      toast.success('Address copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      received_by_dac: 'bg-blue-500',
      pending: 'bg-slate-500',
      in_transit: 'bg-orange-500',
      arrived_in_guyana: 'bg-purple-500',
      delivered: 'bg-emerald-500'
    };
    return colors[status] || 'bg-slate-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      received_by_dac: 'At Warehouse',
      in_transit: 'In Transit',
      arrived_in_guyana: 'Arrived in Guyana',
      delivered: 'Delivered'
    };
    return labels[status] || status.replace(/_/g, ' ').toUpperCase();
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    const msg = feedbackMsg.trim();
    if (!msg) { toast.error('Please write your feedback'); return; }
    setSubmittingFeedback(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/customer/feedback`, { message: msg }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Thank you! Your anonymous feedback was sent.');
      setFeedbackMsg('');
      setShowFeedback(false);
    } catch {
      toast.error('Failed to send feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-white/95 rounded-lg p-2 shadow-lg">
              <img src="/dac-logo.png" alt="DAC Shipping" className="h-12 w-auto object-contain" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm">Welcome, {user?.name}</span>
            <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
              <DialogTrigger asChild>
                <Button
                  data-testid="edit-profile-btn"
                  variant="outline"
                  size="sm"
                  className="border-white text-white hover:bg-white hover:text-slate-900"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Your Profile</DialogTitle>
                  <DialogDescription>Update your contact information</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      data-testid="edit-name"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      data-testid="edit-phone"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Address (Guyana)</Label>
                    <Input
                      data-testid="edit-address"
                      value={profileForm.address}
                      onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowEditProfile(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                      Save Changes
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
              <DialogTrigger asChild>
                <Button
                  data-testid="feedback-btn"
                  variant="outline"
                  size="sm"
                  className="border-white text-white hover:bg-white hover:text-slate-900"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Feedback
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="feedback-dialog">
                <DialogHeader>
                  <DialogTitle>Send Feedback</DialogTitle>
                  <DialogDescription>Share your thoughts with us. Your feedback is 100% anonymous.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmitFeedback} className="space-y-4">
                  <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Submitted anonymously — your name won't be shown.</span>
                  </div>
                  <textarea
                    data-testid="feedback-textarea"
                    className="w-full min-h-[140px] p-3 border border-slate-200 rounded-lg text-sm bg-[#060A14] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-300"
                    placeholder="Tell us what you think..."
                    value={feedbackMsg}
                    onChange={e => setFeedbackMsg(e.target.value)}
                    required
                  />
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowFeedback(false)}>Cancel</Button>
                    <Button type="submit" data-testid="feedback-submit-btn" className="bg-orange-500 hover:bg-orange-600" disabled={submittingFeedback}>
                      {submittingFeedback ? 'Sending...' : 'Send Feedback'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            <Button
              data-testid="logout-btn"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-white text-white hover:bg-white hover:text-slate-900"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* US Address Card */}
        <Card className="address-card" data-testid="us-address-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="h-6 w-6 text-orange-500" />
                <CardTitle className="text-2xl">Your US Shipping Address</CardTitle>
              </div>
              <Button
                data-testid="copy-address-btn"
                onClick={copyAddress}
                variant="outline"
                size="sm"
                className="border-slate-900"
              >
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="font-mono text-lg space-y-1">
              <p className="font-bold text-slate-900">{user?.us_address?.customer_name}</p>
              <p>{user?.us_address?.address_line1}</p>
              <p className="text-orange-600 font-semibold">{user?.us_address?.address_line2}</p>
              <p>{user?.us_address?.city}, {user?.us_address?.state} {user?.us_address?.zip}</p>
              <p className="font-semibold">{user?.us_address?.country || 'USA'}</p>
              <p className="text-slate-600">Phone: {user?.us_address?.contact || '+1 592 635 0592'}</p>
            </div>
            <p className="text-sm text-slate-600 mt-4">Use this address when shopping online</p>
            
            {/* Important Reminders */}
            <div className="mt-6 space-y-3">
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                <p className="text-sm font-semibold text-orange-800 mb-2">⚠️ HOW TO ENTER YOUR SHIPPING ADDRESS</p>
                <p className="text-xs text-slate-600 mb-3">When shopping online, enter your address exactly like this:</p>
                <div className="bg-white rounded-lg p-3 border border-orange-200 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 text-xs">
                    <span className="font-bold text-slate-700">Full Name:</span>
                    <span className="font-mono text-slate-900">Your Name <span className="text-orange-600 font-bold">DAC</span></span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 text-xs">
                    <span className="font-bold text-slate-700">Address 1:</span>
                    <span className="font-mono text-slate-900">836 NE 30th CT Building B</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 text-xs">
                    <span className="font-bold text-slate-700">Address 2:</span>
                    <span className="font-mono text-blue-700 font-bold">DAC + Your Unique Code (e.g. {user?.us_address?.unique_id || user?.us_address?.address_line2})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 text-xs">
                    <span className="font-bold text-slate-700">City:</span>
                    <span className="font-mono text-slate-900">Oakland Park</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 text-xs">
                    <span className="font-bold text-slate-700">State:</span>
                    <span className="font-mono text-slate-900">Florida (FL)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 text-xs">
                    <span className="font-bold text-slate-700">Zip Code:</span>
                    <span className="font-mono text-slate-900">33334-2625</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 text-xs">
                    <span className="font-bold text-slate-700">Country:</span>
                    <span className="font-mono text-slate-900">United States (USA)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-1 text-xs">
                    <span className="font-bold text-slate-700">Phone:</span>
                    <span className="font-mono text-slate-900">+1 592 635 0592</span>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <p className="text-sm font-semibold text-red-800 mb-2">⛔ IMPORTANT REMINDERS</p>
                <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-4">
                  <li>You <strong className="text-red-700">MUST</strong> add <strong className="text-red-700">DAC</strong> at the end of your name (e.g. "John Smith DAC")</li>
                  <li>You <strong className="text-red-700">MUST</strong> include <strong className="font-mono text-blue-700">DAC + your unique code</strong> on Address Line 2 (e.g. {user?.us_address?.unique_id || user?.us_address?.address_line2})</li>
                  <li>Don't forget <strong className="text-red-700">Building B</strong> in Address Line 1</li>
                  <li><strong>Missing any of these details may cause delays</strong> in receiving your package</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Packages Section */}
        <Card data-testid="packages-section">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-3">
              <Package className="h-6 w-6 text-orange-500" />
              My Packages
            </CardTitle>
            <CardDescription>Track all your shipments</CardDescription>
          </CardHeader>
          <CardContent>
            {packages.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No packages yet</p>
            ) : (
              <div className="space-y-4">
                {packages.map((pkg) => (
                  <div
                    key={pkg.package_id}
                    data-testid={`package-item-${pkg.tracking_number}`}
                    data-photo-path={pkg.photo_path || 'NONE'}
                    className="border rounded-lg p-4 hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-start mb-3 gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        {pkg.photo_path ? (
                          <img
                            src={`${API}/files/${pkg.photo_path}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                            alt="Package"
                            data-testid={`package-photo-${pkg.tracking_number}`}
                            onClick={() => window.open(`${API}/files/${pkg.photo_path}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank')}
                            className="h-16 w-16 object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-orange-400 transition cursor-pointer flex-shrink-0"
                          />
                        ) : null}
                        <div className="min-w-0">
                          <p className="font-mono font-semibold text-orange-600 break-all">{pkg.tracking_number}</p>
                          <p className="text-sm text-slate-600">Carrier: {pkg.carrier}</p>
                        </div>
                      </div>
                      <Badge className={getStatusColor(pkg.status)}>
                        {getStatusLabel(pkg.status)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><span className="font-semibold">Weight:</span> {pkg.weight}</p>
                      <p><span className="font-semibold">Dimensions:</span> {pkg.dimensions}</p>
                      {pkg.estimated_delivery && (
                        <p className="col-span-2"><span className="font-semibold">Est. Delivery:</span> {pkg.estimated_delivery}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoices Section */}
        <Card data-testid="invoices-section">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-3">
              <FileText className="h-6 w-6 text-orange-500" />
              My Invoices
            </CardTitle>
            <CardDescription>View your billing history</CardDescription>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No invoices yet</p>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.invoice_id}
                    data-testid={`invoice-item-${invoice.invoice_number}`}
                    className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 hover:shadow-sm transition-all bg-white"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-mono font-bold text-slate-900">{invoice.invoice_number}</p>
                          <p className="text-xs text-slate-500">Issued: {formatInvoiceDate(invoice.created_at)}</p>
                          <p className="text-sm text-slate-500">Due: {formatInvoiceDate(invoice.due_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-900">${invoice.amount.toFixed(2)}</p>
                          <Badge className={`text-xs ${invoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                            {invoice.status === 'paid' ? 'Paid' : 'Unpaid'}
                          </Badge>
                        </div>
                        <Button
                          data-testid={`view-invoice-${invoice.invoice_number}`}
                          variant="outline"
                          size="sm"
                          onClick={() => setViewInvoice(invoice)}
                          className="border-slate-300 hover:bg-slate-50"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice Detail Modal */}
        <Dialog open={!!viewInvoice} onOpenChange={(open) => { if (!open) setViewInvoice(null); }}>
          <DialogContent className="max-w-lg p-0 max-h-[92vh] overflow-y-auto">
            <DialogTitle className="sr-only">Invoice Details</DialogTitle>
            {viewInvoice && (
              <div data-testid="invoice-detail-modal">
                {/* Invoice Header */}
                <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-[#0C1427] via-[#0C1427] to-[#1D4ED8]/30">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="bg-white/95 rounded-lg p-1.5"><img src="/dac-logo.png" alt="DAC" className="h-9 w-9 object-contain" /></div>
                      <div>
                        <h2 className="text-lg font-black tracking-tight text-white leading-none">DAC SHIPPING</h2>
                        <p className="text-slate-400 text-[11px] mt-1">USA → Guyana Shipping</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Invoice</p>
                      <p className="text-xl font-mono font-bold text-[#FF6B35]">{viewInvoice.invoice_number}</p>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF6B35] via-[#2563EB] to-transparent" />
                </div>

                {/* Invoice Body */}
                <div className="px-6 py-5 space-y-5">
                  {/* Status & Date Row */}
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <Badge className={`text-sm px-3 py-1 ${viewInvoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                      {viewInvoice.status === 'paid' ? 'Paid' : 'Payment Due'}
                    </Badge>
                    <div className="flex gap-2">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Issued</p>
                        <p className="text-sm font-semibold text-slate-100">{formatInvoiceDate(viewInvoice.created_at)}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Due</p>
                        <p className="text-sm font-semibold text-amber-300">{formatInvoiceDate(viewInvoice.due_date)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Bill To */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1">Bill To</p>
                    <p className="font-semibold text-slate-900">{userProfile?.name || user?.name}</p>
                    <p className="text-sm text-slate-600">{userProfile?.email || user?.email}</p>
                  </div>

                  {/* Tracking Numbers */}
                  {viewInvoice.tracking_numbers && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <p className="text-xs uppercase tracking-wider text-blue-500 font-medium mb-1">Packages</p>
                      <p className="text-sm font-mono text-blue-800">{viewInvoice.tracking_numbers}</p>
                    </div>
                  )}

                  {/* Line Items Table */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Description</th>
                          <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewInvoice.items?.map((item, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="py-3 px-4 text-sm text-slate-700">{item.description}</td>
                            <td className={`py-3 px-4 text-sm font-medium text-right ${Number(item.amount) < 0 ? 'text-emerald-600' : 'text-slate-900'}`}>{Number(item.amount) < 0 ? `-$${Math.abs(Number(item.amount)).toFixed(2)}` : `$${Number(item.amount).toFixed(2)}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {/* Total */}
                    <div className="bg-gradient-to-r from-[#0C1427] to-[#1D4ED8]/40 px-4 py-3.5 flex justify-between items-center border-t border-slate-200">
                      <span className="text-white font-semibold text-sm">Total Due</span>
                      <span className="text-[#FF6B35] text-2xl font-black">${viewInvoice.amount.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Footer Info */}
                  <div className="text-center text-xs text-slate-400 pt-2 border-t border-slate-100 space-y-0.5">
                    <p className="font-medium text-slate-500">DAC SHIPPING</p>
                    <p>22 Shell Road, Kitty, Georgetown, Guyana</p>
                    <p>WhatsApp: +1 592 635 0592</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CustomerDashboard;